// ===========================================
// Slot Filters
// ===========================================
// Client-side filtering of server-generated availability slots.
//
// ⚠️ Neither ADMIN slot generator applies a past-date filter:
//   - extras/admin  (`extraLesson.service.ts` adminGetAvailableTeacherSlots)
//     has no temporal check whatsoever — ask it for a date years ago and it
//     returns a full grid, every slot `isAvailable: true` unless booked over.
//   - hybrid/admin  (`hybridBooking.service.ts` getAdminAvailableSlots)
//     bounds its per-day loop to the term only, and deliberately skips the
//     24-hour-notice check that gates the parent-facing generator.
//
// So on admin surfaces the frontend is the ONLY guard against placing a
// booking on a day that has already passed. Wherever a DESTINATION week can be
// part-past (see WeekTilePicker's `pastRule`), the elapsed days must be
// filtered here or the relaxed week rule would offer them as bookable.
//
// ⚠️ **`withoutPastDays` HAS NO PRODUCTION CALLERS as of 2026-09-19.** Both of
// its users — `HybridPreBookDialog` and `AdminHybridPreBookingPage` — dropped it
// when admin pre-booking was deliberately opened to past dates (MNM request 4:
// recording a 1:1 arranged off-platform and never entered). That is not an
// oversight in those callers: `createAdminPreBooking` is documented as existing
// for back-dating, suppresses the family confirmation for a past date, and
// writes a CLINIC_PRE_BOOKED audit row.
//
// The function and its tests are KEPT because the fact above — that no admin
// slot generator filters by date — is still true, and the extras/admin surface
// still needs this guard if it is ever opened the same way (MNM request 3,
// which is blocked on extra lessons having NO audit path at all). If you are
// reading this because the file looks dead: it is dormant, not obsolete.
// Deleting it would also delete the only executable record of that rule.

import { TimeSlot } from '../api/hybridBooking.api';
import { parseUTCDate, startOfTodayUTC, schoolTodayUtcMidnight } from './dateUtils';

/**
 * Drop slots whose calendar DAY has already elapsed.
 *
 * Day-level granularity is deliberate: admins legitimately book at short
 * notice, including earlier today (recording a session after the fact), which
 * is why the admin generators skip the 24-hour-notice rule. Filtering by the
 * current clock would remove capability staff rely on — only whole past days
 * are removed.
 *
 * `dateKey` is a tz-stable YYYY-MM-DD, parsed to UTC midnight to match
 * `startOfTodayUTC()`; comparing local-parsed dates here would shift the
 * boundary by a day west of UTC.
 */
export function withoutPastDays(slots: TimeSlot[] | undefined): TimeSlot[] {
  if (!slots || slots.length === 0) return [];
  const today = startOfTodayUTC();
  return slots.filter((slot) => {
    if (!slot.dateKey) return true; // no date to judge — leave it to the caller
    return parseUTCDate(slot.dateKey) >= today;
  });
}

/**
 * Has this slot's day already passed **for the school**?
 *
 * The semantic inverse of `withoutPastDays`' per-slot test, and the predicate
 * behind the "this records history" notice on the admin pre-booking surfaces.
 *
 * ⚠️ **TAKES THE SCHOOL'S ZONE, and that is the whole point.** The first version
 * of this logic (2026-09-19) lived inline in both surfaces and used
 * `startOfTodayUTC()`. The BACKEND decides the same question with
 * `schoolTodayUtcMidnight(timezone)` — for the family-confirmation skip
 * (`hybridBooking.service.ts`, `confirmationSkipReason`) and for the audit gate
 * (`auditRetroactiveClinicEdit`). The two anchors disagree for ~10 hours every
 * Sydney morning, so at 09:00 Sydney a slot dated yesterday read as NOT past on
 * screen while the server treated it as past: the admin saw no notice, the
 * family got no confirmation, and a `CLINIC_PRE_BOOKED` row appeared against
 * their name — every consequence the notice exists to disclose, happening
 * silently. Found by `/qa`, 2026-09-19.
 *
 * ⚠️ **It is EXTRACTED rather than inline for a second reason**: as a useMemo
 * body it was unreachable by test. The source-text pin that guarded the feature
 * matched the *name* gating the render, so inverting the comparison (`<` → `>`)
 * left the whole suite green with the feature dead — measured. A pure function
 * is testable with `vi.setSystemTime`, which is what actually guards it now.
 *
 * Both admin surfaces import this; do NOT re-inline it.
 */
export function slotDayIsPastForSchool(
  slot: Pick<TimeSlot, 'dateKey'> | null | undefined,
  timeZone: string,
  now: Date = new Date()
): boolean {
  if (!slot?.dateKey) return false; // no date to judge — never warn on nothing
  return parseUTCDate(slot.dateKey) < schoolTodayUtcMidnight(timeZone, now);
}
